class ProductMedia extends HTMLElement {
  constructor() {
    super();

    this.selectors = {
      slider: '[data-slider]',
      mediaItem: '[data-media-id]',
      modalOpeners: '[data-modal-opener-id]',
      zoomImages: '[data-pswp-image]',
      btnXr: '[data-shopify-xr]'
    };

    const sliderElement = this.querySelector(this.selectors.slider);
    this.selectedMediaIndex =
      Number(
        sliderElement?.querySelector('[data-selected]')?.dataset.index
      ) || 0;

    this.photoSwipeLightboxInstance = null;
    this.__assetsPromise = null;
    this.__initializing = false;

    this.addEventListener('dragstart', event => {
      event.preventDefault();
    });

    if (Shopify.designMode) {
      this.addEventListener('shopify:section:load', () => {
        this.init();
      });
    }

    this.configureSliderSettings();
  }

  configureSliderSettings() {
    const sliderElement = this.querySelector(this.selectors.slider);
    const withThumbs = this.dataset.withThumbs === 'true';
    let swiperOptions = {};

    if (sliderElement) {
      try {
        swiperOptions = JSON.parse(
          sliderElement.getAttribute('data-swiper-options') || '{}'
        );
      } catch (error) {
        swiperOptions = {};
        console.error('Failed to parse product media swiper options', error);
      }
    }

    const thumbsDirectionDesktop =
      swiperOptions.thumbsDirectionDesktop || 'vertical';
    const slidesPerViewDesktop =
      Number(swiperOptions.slidesPerViewDesktop) || 1;

    this.settings = {
      sliderElement,
      sliderInstance: null,
      options: {
        initialSlide: 0,
        navigation: {
          prevEl: this.querySelector('.swiper-button--prev'),
          nextEl: this.querySelector('.swiper-button--next')
        },
        pagination: {
          el: '.swiper-pagination',
          type: 'progressbar'
        },
        spaceBetween: 2,
        watchOverflow: true,
        watchSlidesVisibility: true,
        watchSlidesProgress: true,
        preventInteractionOnTransition: true,
        autoHeight: this.dataset.autoHeight === 'true',
        on: {
          afterInit: swiper => {
            const isSwiperGenerated = swiper.slides.length > 1;

            if (isSwiperGenerated) {
              const firstSlide = swiper.slides[0];
              const firstSlideVideo = firstSlide.querySelector('video');

              if (firstSlideVideo) {
                this.playPromise(firstSlideVideo);
              }

              const firstSlideYoutube =
                firstSlide.querySelector('.js-youtube');
              if (firstSlideYoutube) {
                this.playYoutubeVideo(firstSlideYoutube);
              }

              const firstSlideVimeo =
                firstSlide.querySelector('.js-vimeo');
              if (firstSlideVimeo) {
                this.playVimeoVideo(firstSlideVimeo);
              }
            }
          },

          slideChangeTransitionEnd: swiper => {
            this.setActiveModalOpener(swiper);
          },

          transitionStart: swiper => {
            const videos = swiper.el.querySelectorAll('video');
            Array.from(videos).forEach(video => {
              video.pause();
            });
          },

          slideChange: swiper => {
            if (window.innerWidth >= 990) {
              const activeIndex = swiper.activeIndex;
              const totalSlides = swiper.slides.length;
              const previousIndex = swiper.previousIndex;

              const prevButton = document.querySelector(
                '.swiper-button.swiper-button--prev'
              );
              const swiperContainer = document.querySelector(
                '.product__media .swiper'
              );

              if (prevButton) {
                prevButton.addEventListener('click', () => {
                  if (swiperContainer) {
                    swiperContainer.style.marginInlineStart = '0';
                  }
                });
              }

              if (swiperContainer) {
                swiperContainer.style.marginInlineStart = '0';
              }

              if (previousIndex > activeIndex) {
                if (swiperContainer) {
                  swiperContainer.style.marginInlineStart = '0';
                }
              }

            }
          },

          transitionEnd: swiper => {
            const isSwiperGenerated = swiper.slides.length > 1;

            if (isSwiperGenerated) {
              const activeSlide = Array.from(swiper.slides).find(slide =>
                slide.classList.contains('swiper-slide-active')
              );

              if (!activeSlide) {
                return;
              }

              const activeSlideVideo = activeSlide.querySelector('video');
              const nextSlideVideo =
                activeSlide.nextElementSibling?.querySelector('video');

              const playVideo = video => {
                if (!video) return;
                if (video.paused) {
                  this.playPromise(video);
                } else {
                  video.pause();
                }
              };

              playVideo(activeSlideVideo);
              playVideo(nextSlideVideo);

              const activeSlideYoutube =
                activeSlide.querySelector('.js-youtube');
              if (activeSlideYoutube) {
                this.playYoutubeVideo(activeSlideYoutube);
              }

              const activeSlideVimeo =
                activeSlide.querySelector('.js-vimeo');
              if (activeSlideVimeo) {
                this.playVimeoVideo(activeSlideVimeo);
              }
            }
          }
        }
      }
    };

    if (withThumbs) {
      const thumbSettings = {
        swiper: {
          el: '.swiper-thumbs',
          initialSlide: this.selectedMediaIndex,
          centeredSlides: true,
          centeredSlidesBounds: true,
          freeMode: {
            enabled: true,
            sticky: true
          },
          direction: 'horizontal',
          slidesPerView: 6,
          spaceBetween: 2,
          threshold: 5,
          watchOverflow: true,
          watchSlidesVisibility: true,
          watchSlidesProgress: true,
          breakpoints: {
            750: {
              direction: thumbsDirectionDesktop,
              spaceBetween: 5,
              slidesPerView: 5
            }
          },
          on: {
            touchEnd: function (swiperInstance) {
              const range = 5;
              const diff = swiperInstance.isHorizontal()
                ? swiperInstance.touches.currentX -
                  swiperInstance.touches.startX
                : swiperInstance.touches.currentY -
                  swiperInstance.touches.startY;
              if (diff < range && diff > -range) {
                swiperInstance.allowClick = true;
              }
            },
            afterInit: () => {
              const thumbs = this.querySelector('.swiper-thumbs');
              if (thumbs) {
                thumbs.removeAttribute('style');
              }
            }
          }
        }
      };

      this.settings.options.thumbs = thumbSettings;
      this.settings.options.breakpoints = {
        990: {
          slidesPerView: slidesPerViewDesktop
        }
      };

      this.settings.options.on.init = swiper => {
        setTimeout(() => {
          if (swiper.thumbs && swiper.thumbs.swiper) {
            swiper.thumbs.swiper.el.classList.add('swiper-thumbs--visible');
          }
        }, 10);
      };

      this.settings.options.on.slideChangeTransitionStart = swiper => {
        if (!swiper.thumbs || !swiper.thumbs.swiper) return;
        const thumbsSwiper = swiper.thumbs.swiper;
        const activeIndex = swiper.activeIndex;
        if (activeIndex !== thumbsSwiper.activeIndex) {
          thumbsSwiper.slideTo(activeIndex);
        }
      };
    } else {
      this.settings.options.slidesPerView = 1;
    }
  }

  hasRequiredLibraries() {
    return (
      typeof window.PhotoSwipeLightbox !== 'undefined' &&
      typeof window.PhotoSwipe !== 'undefined' &&
      typeof window.Swiper !== 'undefined'
    );
  }

  waitForLibraries() {
    if (this.hasRequiredLibraries()) {
      return Promise.resolve();
    }

    const loader = window.themeLibraryLoader;
    if (!loader) {
      return Promise.resolve();
    }

    if (this.__assetsPromise) {
      return this.__assetsPromise;
    }

    const tasks = [];

    if (typeof loader.request === 'function') {
      tasks.push(loader.request('photoswipe'));
      tasks.push(loader.request('swiper'));
    }

    if (typeof loader.whenPhotoSwipeReady === 'function') {
      tasks.push(loader.whenPhotoSwipeReady());
    }

    if (typeof loader.whenSwiperReady === 'function') {
      tasks.push(loader.whenSwiperReady());
    }

    if (!tasks.length) {
      return Promise.resolve();
    }

    this.__assetsPromise = Promise.all(tasks)
      .catch(error => {
        console.error('Failed to load product media libraries', error);
      })
      .finally(() => {
        this.__assetsPromise = null;
      });

    return this.__assetsPromise;
  }

  async init() {
    if (this.__initializing) {
      return;
    }
    this.__initializing = true;

    try {
      await this.waitForLibraries();
    } finally {
      this.__initializing = false;
    }

    if (!this.hasRequiredLibraries()) {
      return;
    }

    this.configureSliderSettings();
    this.mountGallery();
  }

  mountGallery() {
    this.settings.sliderElement = this.querySelector(this.selectors.slider);

    if (!this.settings.sliderElement) {
      return;
    }

    if (this.photoSwipeLightboxInstance) {
      this.photoSwipeLightboxInstance.destroy();
      this.photoSwipeLightboxInstance = null;
    }

    if (this.settings.sliderInstance) {
      this.settings.sliderInstance.destroy(true, true);
      this.settings.sliderInstance = null;
    }

    const [
      closeIcon,
      prevIcon,
      nextIcon
    ] = [
      this.querySelector('[data-close-icon]'),
      this.querySelector('[data-prev-icon]'),
      this.querySelector('[data-next-icon]')
    ];

    const isPhone = () => window.innerWidth < 750;

    const photoSwipeLightboxInstance = new PhotoSwipeLightbox({
      gallery: `#${this.settings.sliderElement.id}`,
      children: 'a',
      mainClass: 'pswp--product-media-gallery',
      loop: false,
      showHideAnimationType: 'zoom',
      initialZoomLevel: zoomLevelObject => {
        return isPhone() ? zoomLevelObject.vFill : zoomLevelObject.fit;
      },
      secondaryZoomLevel: zoomLevelObject => {
        return isPhone() ? zoomLevelObject.fit : 1;
      },
      pswpModule: PhotoSwipe
    });

    photoSwipeLightboxInstance.addFilter('uiElement', (element, data) => {
      if (data.name === 'close' && closeIcon) {
        element.innerHTML = closeIcon.innerHTML;
      } else if (data.name === 'arrowPrev' && prevIcon) {
        element.innerHTML = prevIcon.innerHTML;
      } else if (data.name === 'arrowNext' && nextIcon) {
        element.innerHTML = nextIcon.innerHTML;
      }
      return element;
    });

    photoSwipeLightboxInstance.addFilter('itemData', itemData => {
      if (itemData.type === 'html' && itemData.element) {
        return {
          html: itemData.element.dataset.pswpHtml
        };
      }
      return itemData;
    });

    photoSwipeLightboxInstance.on('beforeOpen', () => {
      const videos = this.querySelectorAll('video');
      Array.from(videos).forEach(video => {
        if (typeof video.play === 'function') {
          video
            .play()
            .catch(() => {
              video.pause();
            });
        }
      });
    });

    photoSwipeLightboxInstance.init();
    this.photoSwipeLightboxInstance = photoSwipeLightboxInstance;

    this.settings.sliderInstance = new Swiper(
      this.settings.sliderElement,
      this.settings.options
    );

    const paginationEl =
      this.settings.sliderInstance?.pagination?.el || null;
    if (paginationEl) {
      paginationEl.style.removeProperty('top');
      paginationEl.style.bottom = '0';
    }
  }

  playPromise(video) {
    const playPromise = video.play();
    if (playPromise !== undefined) {
      playPromise.catch(() => {
        video.pause();
      });
    }
  }

  playYoutubeVideo(element) {
    element.contentWindow.postMessage(
      '{"event":"command","func":"playVideo","args":""}',
      '*'
    );
  }

  playVimeoVideo(element) {
    element.contentWindow.postMessage('{"method":"play"}', '*');
  }

  connectedCallback() {
    this.init();
  }

  setActiveMedia(id) {
    const mediaFound = Array.from(
      this.querySelectorAll(this.selectors.mediaItem)
    ).find(media => Number(media.dataset.mediaId) === id);
    if (!mediaFound || !this.settings.sliderInstance) return;
    this.settings.sliderInstance.slideTo(
      Number(mediaFound.dataset.index)
    );
  }

  setActiveModalOpener(swiper) {
    const activeSlide = swiper.slides.find(slide =>
      slide.classList.contains('swiper-slide-active')
    );
    if (!activeSlide) return;
    const activeMediaId = activeSlide.dataset.mediaId;
    this.querySelector(
      `${this.selectors.modalOpeners}.is-active`
    )?.classList.remove('is-active');
    this.querySelector(
      `${this.selectors.modalOpeners} [data-media-id="${activeMediaId}"]`
    )?.parentElement.classList.add('is-active');
    const isModel = activeSlide.dataset.mediaType === 'model';
    if (!isModel) return;
    const btnXr = this.querySelector(this.selectors.btnXr);
    if (btnXr) {
      btnXr.dataset.shopifyModel3dId = activeMediaId;
    }
  }
}

customElements.define('product-media', ProductMedia);
